// src/components/SavingsBar.tsx
import React, { useEffect, useMemo, useState } from 'react';
import { useSavings } from '../store/savings';
import { timeAgo } from '../lib/dataSync';

export default function SavingsBar() {
  const { total7d, lastSavedAt } = useSavings();
  const [dismissed, setDismissed] = useState(false);
  const [ready, setReady] = useState(false);
  const SEEN_KEY = 'bw.savings.lastSeenEventTs';
  const AUTO_HIDE_MS = 1600;

  const visible = useMemo(() => {
    return typeof total7d === 'number' && total7d > 0;
  }, [total7d]);

  useEffect(() => {
    try {
      const seenTs = Number(localStorage.getItem(SEEN_KEY) || 0);
      const currentLast = Number(lastSavedAt || 0);
      if (!currentLast) {
        setDismissed(true);
        setReady(true);
        return;
      }
      // Show only when there is a fresh save event not yet acknowledged.
      setDismissed(seenTs >= currentLast);
    } catch {}
    setReady(true);
  }, [lastSavedAt]);

  useEffect(() => {
    if (!ready || !visible || dismissed || !lastSavedAt) return;
    const timer = window.setTimeout(() => {
      setDismissed(true);
      try {
        localStorage.setItem(SEEN_KEY, String(lastSavedAt));
      } catch {}
    }, AUTO_HIDE_MS);
    return () => window.clearTimeout(timer);
  }, [ready, visible, dismissed, lastSavedAt]);

  if (!ready || !visible || dismissed) return null;

  return (
    <div className="fixed inset-x-0 top-0 z-40 px-3 pointer-events-none">
      <div className="relative mx-auto mt-2 max-w-5xl rounded-2xl border border-white/15 bg-[linear-gradient(135deg,rgba(38,26,54,.95),rgba(74,47,77,.93))] text-white shadow-xl shadow-black/25 backdrop-blur-xl overflow-hidden pointer-events-auto">
        <div className="flex items-center gap-3 px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/65">
            Savings
          </div>
          <div className="flex items-center gap-2 rounded-full border border-emerald-300/30 bg-emerald-400/10 px-3 py-1.5">
            <div className="text-base font-extrabold tabular-nums text-emerald-200">
              +₹{Math.round(total7d)}
            </div>
            <div className="text-[11px] text-white/65">
              Last save {lastSavedAt ? timeAgo(lastSavedAt) : '—'}
            </div>
          </div>
          <button
            className="ml-auto text-sm rounded-full border border-white/20 px-2.5 py-1 text-white/80 hover:bg-white/10 transition"
            onClick={() => {
              setDismissed(true);
              try {
                localStorage.setItem(SEEN_KEY, String(lastSavedAt || Date.now()));
              } catch {}
            }}
            aria-label="Dismiss savings banner"
            title="Dismiss"
          >
            ×
          </button>
        </div>
      </div>
    </div>
  );
}
